import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client.ts";
import type { Domain, PostalInstance, PostalServer } from "../api/types.ts";
import { confirmAction, toast } from "../components/feedback.tsx";
import { Card, Drawer, Empty } from "../components/ui.tsx";

/**
 * Postal installations.
 *
 * This is step one of setup: tell the app where Postal lives, then paste the two
 * URLs it hands back into Postal. Mail servers are added per domain from the
 * Domains screen, because there is exactly one per domain.
 */
export function PostalPage() {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const instances = useQuery({
    queryKey: ["postal-instances"],
    queryFn: () => api.get<PostalInstance[]>("/config/postal-instances"),
  });
  const servers = useQuery({
    queryKey: ["postal-servers"],
    queryFn: () => api.get<PostalServer[]>("/config/postal-servers"),
  });
  const domains = useQuery({
    queryKey: ["domains"],
    queryFn: () => api.get<Domain[]>("/config/domains"),
  });

  const refresh = () => {
    setError(null);
    void qc.invalidateQueries({ queryKey: ["postal-instances"] });
    void qc.invalidateQueries({ queryKey: ["postal-servers"] });
  };

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/config/postal-instances/${id}`),
    onSuccess: () => {
      refresh();
      toast("Postal removed");
    },
    onError: (err: Error) => setError(err.message),
  });

  const removeServer = useMutation({
    mutationFn: (id: string) => api.del(`/config/postal-servers/${id}`),
    onSuccess: () => {
      refresh();
      toast("Server deleted");
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <div className="page">
      <div className="page-header">
        <h1>Postal</h1>
        <p>Where mail actually goes out. Register an installation, then add domains against it.</p>
      </div>

      {error && <div className="error">{error}</div>}

      <SetupSteps />

      <div className="toolbar">
        <button type="button" className="btn" onClick={() => setAdding(true)}>
          + Add installation
        </button>
      </div>

      {adding && (
        <Drawer title="Add a Postal installation" onClose={() => setAdding(false)}>
          <AddInstallation
            onDone={() => {
              setAdding(false);
              void qc.invalidateQueries({ queryKey: ["postal-instances"] });
            }}
          />
        </Drawer>
      )}

      {instances.data?.length === 0 && !adding && (
        <Card>
          <Empty>No Postal installation yet. Add one to start sending.</Empty>
        </Card>
      )}

      {instances.data?.map((instance) => {
        const own = (servers.data ?? []).filter((s) => s.instanceId === instance.id);
        return (
          <Card
            key={instance.id}
            title={instance.name}
            subtitle={instance.baseUrl}
            action={
              <button
                type="button"
                className="btn ghost small"
                onClick={async () => {
                  if (
                    await confirmAction({
                      title: `Remove ${instance.name}?`,
                      message:
                        "Mail servers no domain uses go with it. This is refused while any domain still sends through it.",
                      confirmLabel: "Remove Postal",
                      destructive: true,
                    })
                  )
                    remove.mutate(instance.id);
                }}
              >
                Delete
              </button>
            }
          >
            <Endpoints instanceId={instance.id} />

            <table>
              <thead>
                <tr>
                  <th>Mail server</th>
                  <th>Domain</th>
                  <th>Key</th>
                  <th>Credential</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {own.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ color: "var(--gray-500)" }}>
                      None yet — they are created when you add a domain.
                    </td>
                  </tr>
                ) : (
                  own.map((server) => {
                    const domain = domains.data?.find((d) => d.postalServerId === server.id);
                    return (
                      <tr key={server.id}>
                        <td style={{ fontWeight: 600 }}>{server.name}</td>
                        <td className="mono">
                          {domain ? (
                            <Link to="/settings/domains">{domain.name}</Link>
                          ) : (
                            <span className="pill warn">No domain</span>
                          )}
                        </td>
                        <td className="mono">{server.apiKeyHint}</td>
                        <td>
                          {server.lastVerifiedAt ? (
                            <span className="pill ok">Verified</span>
                          ) : (
                            <span className="pill bad" title={server.lastVerifyError ?? ""}>
                              Not verified
                            </span>
                          )}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {domain ? (
                            /* Deleting the domain removes this with it. */
                            <span className="hint">removed with its domain</span>
                          ) : (
                            <button
                              type="button"
                              className="btn ghost small"
                              onClick={async () => {
                                if (
                                  await confirmAction({
                                    title: `Delete ${server.name}?`,
                                    message:
                                      "No domain uses it, so this only removes the stored credential.",
                                    confirmLabel: "Delete server",
                                    destructive: true,
                                  })
                                )
                                  removeServer.mutate(server.id);
                              }}
                            >
                              Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </Card>
        );
      })}
    </div>
  );
}

/** The order that actually works, since each step depends on the last. */
function SetupSteps() {
  return (
    <Card>
      <ol className="steps">
        <li>
          <strong>Register your Postal installation</strong> here. You get a webhook URL and an
          inbound URL.
        </li>
        <li>
          <strong>In Postal, create one mail server per sending domain.</strong> On each, add the
          webhook URL with every event enabled, and an HTTP endpoint route to the inbound URL in
          <em> processed JSON</em> format. Copy the server&rsquo;s API key.
        </li>
        <li>
          <strong>Add the domain</strong> under Domains &amp; senders, pasting that API key. The
          mail server is registered in the same step.
        </li>
      </ol>
    </Card>
  );
}

/** The two URLs to paste into Postal, with copy buttons. */
function Endpoints({ instanceId }: { instanceId: string }) {
  const endpoints = useQuery({
    queryKey: ["postal-endpoints", instanceId],
    queryFn: () =>
      api.get<{ webhookUrl: string; inboundUrl: string | null }>(
        `/config/postal-instances/${instanceId}/endpoints`,
      ),
  });

  if (!endpoints.data) return null;

  return (
    <div className="card-body endpoints">
      <CopyField label="Webhook URL" value={endpoints.data.webhookUrl} />
      {endpoints.data.inboundUrl && (
        <CopyField
          label="Inbound route URL"
          value={endpoints.data.inboundUrl}
          hint="Contains a secret. Paste it into Postal; don't share it elsewhere."
        />
      )}
    </div>
  );
}

function CopyField({ label, value, hint }: { label: string; value: string; hint?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="field">
      {/* biome-ignore lint/a11y/noLabelWithoutControl: labels a read-only value, not an input. */}
      <label>{label}</label>
      <div className="copy-row">
        <code className="mono">{value}</code>
        <button
          type="button"
          className="btn ghost small"
          onClick={async () => {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      {hint && <div className="hint">{hint}</div>}
    </div>
  );
}

function AddInstallation({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState("");
  const [baseUrl, setBaseUrl] = useState("https://");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    // The inbound secret is generated server-side; nobody should invent one.
    mutationFn: () => api.post("/config/postal-instances", { name, baseUrl }),
    onSuccess: () => {
      onDone();
      toast("Postal connected");
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <Card>
      <form
        className="card-body"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          setError(null);
          create.mutate();
        }}
      >
        {error && <div className="error">{error}</div>}
        <div className="row">
          <div className="field">
            <label htmlFor="pi-name">Name</label>
            <input
              id="pi-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="primary"
              required
            />
          </div>
          <div className="field">
            <label htmlFor="pi-url">Postal URL</label>
            <input
              id="pi-url"
              className="mono"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://postal.example.com"
              required
            />
            <div className="hint">
              The address of your Postal web interface. Webhook signatures are checked against its
              published keys.
            </div>
          </div>
        </div>
        <button className="btn" type="submit" disabled={create.isPending}>
          Add installation
        </button>
      </form>
    </Card>
  );
}
